/**
 * 图片高清增强共享工具
 * 供 QuillEditorWithImageResize（编辑页）与 QuizQuestionTitle（预览页缩放面板）复用：
 * - loadImageForEnhance: 四级回退加载图片（storage.download → fetch → crossOrigin Image → 普通 Image）
 * - algorithmEnhanceImage: 2 倍平滑放大 + unsharp 锐化 + webp 0.95 导出
 * - uploadHdBlob: 上传 paper-images 桶，返回公开 URL
 * - enhanceImageAlgorithmically: 一键高清全流程（加载+增强+上传），返回新 URL
 * - enhanceImageByAi: AI 超清（ai-image-hd 云函数，wan2.7-image 2K），返回新 URL
 */
import { supabase, getSupabaseUrl } from '../supabase/client';

const logPrefix = '[高清增强]';

/** URL 同源化：入库 src 可能是生产域名绝对 URL（https://udo100.com/sb-api/...），预览环境跨域会被拦截，重写为相对路径 */
export function toSameOriginSrc(originalSrc: string): string {
  const sbApiIdx = originalSrc.indexOf('/sb-api/');
  if (sbApiIdx > 0) return originalSrc.substring(sbApiIdx);
  return originalSrc;
}

function loadImageEl(src: string, useCors: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    if (useCors) im.crossOrigin = 'anonymous';
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('load failed'));
    im.src = src;
  });
}

/**
 * 四级回退加载图片，最大限度避免预览/生产环境的跨域加载失败：
 * a. supabase.storage.download（从 URL 解析 bucket/path，走 client 通路，预览/生产均可用）
 * b. fetch → blob → objectURL
 * c. crossOrigin='anonymous' 的 Image
 * d. 普通 Image（可能污染 canvas，导出环节若失败会有明确报错）
 */
export async function loadImageForEnhance(originalSrc: string): Promise<HTMLImageElement> {
  const loadSrc = toSameOriginSrc(originalSrc);
  let sourceImg: HTMLImageElement | null = null;

  // a. supabase.storage.download
  try {
    const m = loadSrc.match(/\/storage\/v1\/object\/(?:public\/)?([^/?#]+)\/([^?#]+)/);
    if (m) {
      const [, bucket, objectPath] = m;
      const { data: dlBlob, error: dlError } = await supabase.storage
        .from(bucket)
        .download(decodeURIComponent(objectPath));
      if (dlError) console.debug(logPrefix, 'storage.download 失败:', dlError.message);
      if (!dlError && dlBlob) {
        const objectUrl = URL.createObjectURL(dlBlob);
        try {
          sourceImg = await loadImageEl(objectUrl, false);
        } finally {
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
        }
      }
    }
  } catch { /* download 失败，走回退 */ }

  // b. fetch → blob → objectURL
  if (!sourceImg) {
    try {
      const resp = await fetch(loadSrc, { mode: 'cors', credentials: 'omit' });
      if (resp.ok) {
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        try {
          sourceImg = await loadImageEl(objectUrl, false);
        } finally {
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
        }
      }
    } catch { /* fetch 失败，走回退 */ }
  }

  // c/d. 回退：直接用同源化后的 URL 加载
  if (!sourceImg) {
    console.debug(logPrefix, 'storage.download/fetch 均未成功，回退直接加载:', loadSrc);
    try {
      sourceImg = await loadImageEl(loadSrc, true);
    } catch {
      sourceImg = await loadImageEl(loadSrc, false);
    }
  }
  return sourceImg;
}

/** 自适应放大倍数：小图放大更多以提升清晰度，大图保持 2 倍避免文件过大 */
function pickScale(w: number, h: number): number {
  const minSide = Math.min(w, h);
  if (minSide <= 400) return 4;
  if (minSide <= 700) return 3;
  return 2;
}

/** 单轮 unsharp 锐化：叠加"原图 - 模糊图"的高频差值（amount 为锐化强度） */
function unsharpSharpen(canvas: HTMLCanvasElement, amount: number, blurPx: number): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  try {
    const blurCanvas = document.createElement('canvas');
    blurCanvas.width = canvas.width;
    blurCanvas.height = canvas.height;
    const bctx = blurCanvas.getContext('2d');
    if (!bctx) return;
    bctx.filter = `blur(${blurPx}px)`;
    bctx.drawImage(canvas, 0, 0);
    const base = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const blur = bctx.getImageData(0, 0, canvas.width, canvas.height);
    const out = ctx.createImageData(canvas.width, canvas.height);
    for (let i = 0; i < base.data.length; i += 4) {
      for (let c = 0; c < 3; c++) {
        const diff = base.data[i + c] - blur.data[i + c];
        let v = base.data[i + c] + diff * amount;
        if (v < 0) v = 0; else if (v > 255) v = 255;
        out.data[i + c] = v;
      }
      out.data[i + 3] = base.data[i + 3];
    }
    ctx.putImageData(out, 0, 0);
  } catch { /* getImageData 跨域污染时跳过锐化 */ }
}

/** 算法增强管线：自适应放大 → 两轮锐化，导出 webp 0.95 */
export async function algorithmEnhanceImage(sourceImg: HTMLImageElement): Promise<Blob> {
  const sw = sourceImg.naturalWidth;
  const sh = sourceImg.naturalHeight;
  if (sw <= 0 || sh <= 0) throw new Error('无法读取图片尺寸');

  // 1. 自适应放大（小图 3-4 倍、大图 2 倍，平滑高质量插值）
  // 注意：不做放大前预降噪——整图预模糊会先把清晰边缘糊化，锐化无法完全补偿（实测反而更模糊）。
  const scale = pickScale(sw, sh);
  const canvas = document.createElement('canvas');
  canvas.width = sw * scale;
  canvas.height = sh * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法创建canvas');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(sourceImg, 0, 0, canvas.width, canvas.height);

  // 2. 两轮锐化：第一轮高强度恢复边缘对比，第二轮小半径低强度收紧文字线条边缘
  unsharpSharpen(canvas, 0.6, 1.2);
  unsharpSharpen(canvas, 0.35, 0.8);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', 0.95)
  );
  if (!blob) throw new Error('高清导出失败：图片源受跨域限制，无法在浏览器内处理');
  console.debug(logPrefix, `算法增强完成: ${sw}x${sh} -> ${canvas.width}x${canvas.height} (${scale}x)`);
  return blob;
}

/** 上传高清结果到 paper-images 桶，返回公开 URL */
export async function uploadHdBlob(blob: Blob, prefix = 'hd'): Promise<string> {
  const fileName = `paper-editor/${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}.webp`;
  const arrayBuffer = await blob.arrayBuffer();
  const { data: upData, error: upError } = await supabase.storage
    .from('paper-images')
    .upload(fileName, arrayBuffer, { contentType: 'image/webp', upsert: true });
  if (upError) throw upError;
  const { data: { publicUrl } } = supabase.storage.from('paper-images').getPublicUrl(upData.path);
  return publicUrl;
}

/** 一键高清全流程：四级加载 → 算法增强 → 上传，返回新图 URL */
export async function enhanceImageAlgorithmically(originalSrc: string): Promise<string> {
  const sourceImg = await loadImageForEnhance(originalSrc);
  const blob = await algorithmEnhanceImage(sourceImg);
  return uploadHdBlob(blob, 'hd');
}

/** AI 超清：调用 ai-image-hd 云函数（wan2.7-image 2K 超分，服务端转存 paper-images），返回新图 URL */
export async function enhanceImageByAi(originalSrc: string): Promise<string> {
  // AI 上游硬限制：输入图宽高各 ≥240px（实测 242x44 的图报 InvalidParameter "Error validating image"）。
  // 过小的图先在前端放大到合规尺寸并上传为临时输入图（云函数会自动把 URL 归一化为公网存储域名）。
  let effectiveSrc = originalSrc;
  try {
    const probe = await loadImageForEnhance(originalSrc);
    const w = probe.naturalWidth;
    const h = probe.naturalHeight;
    const MIN_SIDE = 240;
    if (w > 0 && h > 0 && (w < MIN_SIDE || h < MIN_SIDE)) {
      const scale = Math.max(MIN_SIDE / w, MIN_SIDE / h, 2);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(probe, 0, 0, canvas.width, canvas.height);
        unsharpSharpen(canvas, 0.4, 1.0);
        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, 'image/webp', 0.95)
        );
        if (blob) {
          effectiveSrc = await uploadHdBlob(blob, 'aihdin');
          console.debug('[AI高清] 原图过小 ' + w + 'x' + h + '，已放大为 ' + canvas.width + 'x' + canvas.height + ' 输入:', effectiveSrc);
        }
      }
    }
  } catch (err) {
    // 预检失败不阻断：仍按原 URL 调用，由云函数预检/上游报错兜底
    console.warn('[AI高清] 输入图尺寸预检失败，按原图继续:', err);
  }

  // AI 服务在服务端拉图，需要公网可达的完整 URL。
  // 预览环境下 origin 是沙箱域名（外部服务器不可达，/sb-api/storage/* 会 502），
  // 必须统一改写为生产访问入口域名（MEOO_CONFIG.meoo_app_access_url）。
  const accessOrigin = String((window as any).MEOO_CONFIG?.meoo_app_access_url || window.location.origin).replace(/\/+$/, '');
  let publicSrc = effectiveSrc;
  if (publicSrc.startsWith('/')) {
    publicSrc = accessOrigin + publicSrc;
  } else {
    try {
      const u = new URL(publicSrc);
      if (u.origin !== accessOrigin) {
        // 沙箱域名 / 未知域名的 URL 一律改写为生产入口域名 + 原路径
        publicSrc = accessOrigin + u.pathname + u.search;
      }
    } catch { /* 非法 URL 保持原样，由后端报参数错误 */ }
  }
  console.debug('[AI高清] 输入图 URL:', publicSrc);

  const session = (await supabase.auth.getSession()).data.session;
  const authHeaders = session ? { Authorization: `Bearer ${session.access_token}` } : {};
  const resp = await fetch(`${getSupabaseUrl()}/functions/v1/ai-image-hd`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authHeaders as Record<string, string>),
    },
    body: JSON.stringify({ imageUrl: publicSrc }),
  });
  const result = await resp.json().catch(() => ({ error: `响应解析失败(${resp.status})` }));
  if (!resp.ok || result.error) {
    console.error('[AI高清] 失败:', resp.status, result.error || result);
    throw new Error(result.error || `AI 高清请求失败(${resp.status})`);
  }
  if (!result.path) throw new Error('AI 高清未返回图片路径');
  const { data: { publicUrl } } = supabase.storage.from('paper-images').getPublicUrl(result.path);
  console.debug('[AI高清] 成功，新图:', publicUrl);
  return publicUrl;
}
